/**
 * RTK Query endpoints for the organizations service (@cluster/organizations).
 * Base path after the dev proxy + nginx rewrite strips `/api`:
 * `/organizations`, `/user-groups`.
 */
import { apiSlice } from './apiSlice';
import type {
  Organization,
  OrganizationFull,
  OrgMember,
  UserGroup,
  UserGroupMember,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

/**
 * Extract the member array from the user-groups members endpoint response.
 *
 * Backend contract (user-groups.controller.ts getMembers) returns
 * `{ data: { group: {...}, members: UserGroupMember[] } }` — the agent-friendly
 * assembly. This extracts `members` with defensive fallbacks for plain-array
 * and direct-`members` shapes.
 */
export function extractUserGroupMembers(response: unknown): UserGroupMember[] {
  const body = response as { data?: unknown } | null;
  const inner = body?.data ?? body;
  const members = (inner as { members?: UserGroupMember[] } | UserGroupMember[] | null) ?? null;
  if (Array.isArray(members)) return members;
  const nested = (members as { members?: UserGroupMember[] } | null)?.members;
  return Array.isArray(nested) ? nested : [];
}

export const organizationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Organizations ─────────────────────────────────────────────────────
    getOrganizations: builder.query<Organization[], void>({
      query: () => '/organizations',
      transformResponse: data<Organization[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Organization' as const, id: _id })),
              { type: 'Organization', id: 'LIST' },
            ]
          : [{ type: 'Organization', id: 'LIST' }],
    }),

    getOrganization: builder.query<Organization, string>({
      query: (id) => `/organizations/${id}`,
      transformResponse: data<Organization>,
      providesTags: (_result, _error, id) => [{ type: 'Organization', id }],
    }),

    /** GET /organizations/:id/full — org + members (ORG-3). */
    getOrganizationFull: builder.query<OrganizationFull, string>({
      query: (id) => `/organizations/${id}/full`,
      transformResponse: data<OrganizationFull>,
      providesTags: (_result, _error, id) => [
        { type: 'OrganizationFull', id },
        { type: 'Organization', id },
      ],
    }),

    createOrganization: builder.mutation<Organization, Partial<Organization>>({
      query: (body) => ({ url: '/organizations', method: 'POST', body }),
      transformResponse: data<Organization>,
      invalidatesTags: [{ type: 'Organization', id: 'LIST' }],
    }),

    updateOrganization: builder.mutation<Organization, { id: string; body: Partial<Organization> }>({
      query: ({ id, body }) => ({ url: `/organizations/${id}`, method: 'PATCH', body }),
      transformResponse: data<Organization>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Organization', id }, { type: 'Organization', id: 'LIST' }]
          : [{ type: 'Organization', id: 'LIST' }],
    }),

    deleteOrganization: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/organizations/${id}`, method: 'DELETE' }),
      invalidatesTags: [
        { type: 'Organization', id: 'LIST' },
        { type: 'OrganizationFull', id: 'LIST' },
      ],
    }),

    addOrgMember: builder.mutation<
      OrgMember,
      { id: string; userId: string; orgRole?: string; roleIds?: string[] }
    >({
      query: ({ id, ...body }) => ({
        url: `/organizations/${id}/members`,
        method: 'POST',
        body,
      }),
      transformResponse: data<OrgMember>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [
              { type: 'OrganizationFull', id },
              { type: 'OrgMember', id },
              { type: 'Organization', id: 'LIST' },
            ]
          : [{ type: 'Organization', id: 'LIST' }],
    }),

    removeOrgMember: builder.mutation<{ success: boolean }, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/organizations/${id}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OrganizationFull', id },
        { type: 'OrgMember', id },
        { type: 'Organization', id: 'LIST' },
      ],
    }),

    // ── User groups ───────────────────────────────────────────────────────
    getUserGroups: builder.query<UserGroup[], { orgId?: string } | void>({
      query: (params) => ({ url: '/user-groups', params: params || undefined }),
      transformResponse: data<UserGroup[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'UserGroup' as const, id: _id })),
              { type: 'UserGroup', id: 'LIST' },
            ]
          : [{ type: 'UserGroup', id: 'LIST' }],
    }),

    getUserGroup: builder.query<UserGroup, string>({
      query: (id) => `/user-groups/${id}`,
      transformResponse: data<UserGroup>,
      providesTags: (_result, _error, id) => [{ type: 'UserGroup', id }],
    }),

    createUserGroup: builder.mutation<UserGroup, Partial<UserGroup>>({
      query: (body) => ({ url: '/user-groups', method: 'POST', body }),
      transformResponse: data<UserGroup>,
      invalidatesTags: [{ type: 'UserGroup', id: 'LIST' }],
    }),

    updateUserGroup: builder.mutation<UserGroup, { id: string; body: Partial<UserGroup> }>({
      query: ({ id, body }) => ({ url: `/user-groups/${id}`, method: 'PATCH', body }),
      transformResponse: data<UserGroup>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'UserGroup', id }, { type: 'UserGroup', id: 'LIST' }]
          : [{ type: 'UserGroup', id: 'LIST' }],
    }),

    deleteUserGroup: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/user-groups/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'UserGroup', id: 'LIST' }],
    }),

    getUserGroupMembers: builder.query<UserGroupMember[], string>({
      query: (id) => `/user-groups/${id}/members`,
      // Backend contract (user-groups.controller.ts getMembers) returns
      // `{ data: { group: {...}, members: UserGroupMember[] } }` — the agent-
      // friendly assembly. Extract `members` (fallback: plain array for
      // compatibility with older/other shapes) so callers get an array.
      // Backend contract (user-groups.controller.ts getMembers) returns
      // `{ data: { group: {...}, members: UserGroupMember[] } }` — the agent-
      // friendly assembly. Extract `members` (fallback: plain array for
      // compatibility with older/other shapes) so callers get an array.
      transformResponse: extractUserGroupMembers,
      providesTags: (_result, _error, id) => [
        { type: 'UserGroup', id },
        { type: 'UserGroupMember', id },
      ],
    }),

    addUserGroupMember: builder.mutation<{ success: boolean }, { id: string; userId: string }>({
      query: ({ id, ...body }) => ({
        url: `/user-groups/${id}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'UserGroup', id },
        { type: 'UserGroupMember', id },
      ],
    }),

    removeUserGroupMember: builder.mutation<
      { success: boolean },
      { id: string; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/user-groups/${id}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'UserGroup', id },
        { type: 'UserGroupMember', id },
      ],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useGetOrganizationFullQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useAddOrgMemberMutation,
  useRemoveOrgMemberMutation,
  useGetUserGroupsQuery,
  useGetUserGroupQuery,
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,
  useGetUserGroupMembersQuery,
  useAddUserGroupMemberMutation,
  useRemoveUserGroupMemberMutation,
} = organizationsApi;